import { useQueryClient } from '@tanstack/vue-query';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import useUserService from '@/composables/apiServices/useUserService';
import useChangeLocale from '@/composables/useChangeLocale';
import { getCollapsedThreadItemState, getMailboxAndSearchListQueriesKeys } from '@/helpers/mailbox';
import type {
  IListMessageResponse,
  IListMessagesResponse,
  IMessageResponse
} from '@/types/services/message.service';
import type { IFolderTreeResponse, IHashedFolderItem } from '@/types/services/folder.service';
import type { IMassActionRequest, IMassActionMessage } from '@/types/services/massAction.service';
import {
  isISearchAllResultsSelection,
  isIMailboxAllResultsSelection
} from '@/types/components/mailbox-shared/mass-actions/shared.consts';
import type { QueryClient, QueryKey } from '@tanstack/vue-query';
import type { CacheSnapshot } from '@/types/composables/mailbox/useOptimisticMassAction';

const isMarkAsAction = (action: string): action is 'seen' | 'unseen' | 'flagged' | 'unflag' => {
  return ['seen', 'unseen', 'flagged', 'unflag'].includes(action);
};

const isMovingAction = (
  action: string
): action is 'moveTo' | 'trash' | 'spam' | 'archive' | 'delete' => {
  return ['moveTo', 'trash', 'spam', 'archive', 'delete'].includes(action);
};

const dedupeQueryKeys = (queryKeys: QueryKey[]): QueryKey[] => {
  const seenKeys = new Set<string>();
  return queryKeys.filter((key) => {
    const keyStr = JSON.stringify(key);
    if (seenKeys.has(keyStr)) return false;
    seenKeys.add(keyStr);
    return true;
  });
};

/** Cancel in-flight fetches for the caches we're about to write to below, so a stale response
 *  can't resolve afterwards and silently overwrite the optimistic update.
 */
async function cancelPendingQueries(
  uniqueMailboxIds: string[],
  queryClient: QueryClient,
  currentUserIndex: string,
  uniqueThreadIds: string[],
  exact = false
) {
  await Promise.all([
    ...uniqueMailboxIds.map((mailboxId) =>
      queryClient.cancelQueries({
        queryKey: ['authenticatedQuery', currentUserIndex, 'mailbox', mailboxId],
        exact
      })
    ),
    queryClient.cancelQueries({
      queryKey: ['authenticatedQuery', currentUserIndex, 'search', 'query'],
      exact
    }),
    queryClient.cancelQueries({
      queryKey: ['authenticatedQuery', currentUserIndex, 'folders'],
      exact: false
    }),
    ...uniqueThreadIds.map((threadId) =>
      queryClient.cancelQueries({
        queryKey: ['authenticatedQuery', currentUserIndex, 'thread', threadId],
        exact: false
      })
    )
  ]);
}

const useOptimisticMassAction = () => {
  const queryClient = useQueryClient();
  const { userIndex } = useAuthenticatedQuery();
  const { languageOptions } = useChangeLocale();
  const { attributes } = useUserService();
  const { data: userAttributes } = attributes();

  const applyOptimisticUpdate = async (postData: IMassActionRequest): Promise<() => void> => {
    const snapshots: CacheSnapshot = [];
    const currentUserIndex = userIndex.value;

    if (!currentUserIndex) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return () => {};
    }

    const isSearchAll = isISearchAllResultsSelection(postData);
    const isAllResults = !isSearchAll && isIMailboxAllResultsSelection(postData);
    const action = postData.action;

    // Collect unique mailbox IDs and message info from the request
    const messages: { mailboxId: string; messageId: number | '*'; threadId?: string }[] = [];

    if (!isSearchAll && 'messages' in postData) {
      for (const msg of postData.messages) {
        if (msg.messageId === '*') {
          messages.push({
            mailboxId: msg.mailboxId,
            messageId: '*'
          });
        } else {
          const massMsg = msg as IMassActionMessage;
          messages.push({
            mailboxId: massMsg.mailboxId,
            messageId: massMsg.messageId,
            threadId: massMsg.threadId
          });
        }
      }
    }

    const uniqueMailboxIds = [...new Set(messages.map((m) => m.mailboxId))];
    const specificMessages = messages.filter((m) => m.messageId !== '*') as {
      mailboxId: string;
      messageId: number;
      threadId?: string;
    }[];
    const uniqueThreadIds = [
      ...new Set(specificMessages.map((m) => m.threadId).filter(Boolean))
    ] as string[];
    const isAllResultsForMailbox = (mailboxId: string) =>
      isAllResults || messages.some((m) => m.mailboxId === mailboxId && m.messageId === '*');

    const isMessageAffected = (msgId: number, msgMailbox: string): boolean => {
      if (isSearchAll) return true;
      if (isAllResultsForMailbox(msgMailbox)) return true;
      return specificMessages.some((m) => m.messageId === msgId && m.mailboxId === msgMailbox);
    };

    // --- MARK-AS ACTIONS ---
    if (isMarkAsAction(action)) {
      await cancelPendingQueries(
        uniqueMailboxIds,
        queryClient,
        currentUserIndex,
        uniqueThreadIds,
        true
      );

      const flagUpdates: Partial<Pick<IMessageResponse, 'seen' | 'flagged'>> = {};
      if (action === 'seen') flagUpdates.seen = true;
      if (action === 'unseen') flagUpdates.seen = false;
      if (action === 'flagged') flagUpdates.flagged = true;
      if (action === 'unflag') flagUpdates.flagged = false;

      // Mark-as-read marks the whole thread as seen, regardless of mailbox, so the
      // post-action thread state is known without inspecting any cache.
      const isWholeThreadSeen = action === 'seen' && postData.context !== 'thread';
      const affectedThreadIds = new Set(uniqueThreadIds);

      // Update the thread caches first, so the collapsed list items can be derived from the
      // threads' post-action states below.
      const threadQueryKeys: QueryKey[] = dedupeQueryKeys(
        uniqueThreadIds.map((threadId) => [
          'authenticatedQuery',
          currentUserIndex,
          'thread',
          threadId
        ])
      );

      for (const queryKey of threadQueryKeys) {
        const oldData = queryClient.getQueryData<IListMessagesResponse>(queryKey);
        if (oldData) {
          snapshots.push({ key: queryKey, data: oldData });
          queryClient.setQueryData<IListMessagesResponse>(queryKey, {
            ...oldData,
            results: oldData.results.map((message) =>
              isWholeThreadSeen || isMessageAffected(message.id, message.mailbox)
                ? { ...message, ...flagUpdates }
                : message
            )
          });
        }
      }

      // In threading view a list item represents the whole thread, so its state is the
      // thread's aggregate (all seen / any flagged). A thread-scoped read is known
      // immediately; the other actions derive it from the just updated thread caches.
      const threadItemStates = new Map<string, { seen: boolean | null; flagged: boolean | null }>();
      let allItemsState: { seen: boolean | null; flagged: boolean | null } | null = null;
      if (userAttributes.value?.threadingShownState) {
        if (isWholeThreadSeen) {
          // A thread-scoped read is certain for every acted-on thread, so no cache check is
          // needed.
          if (specificMessages.length === 0) {
            allItemsState = { seen: true, flagged: null };
          } else {
            for (const threadId of uniqueThreadIds) {
              threadItemStates.set(threadId, { seen: true, flagged: null });
            }
          }
        } else {
          const sureUnseen = action === 'unseen';
          const sureFlagged = action === 'flagged';
          if (specificMessages.length === 0) {
            // All-results action: every thread in the list contains an acted-on message, so the
            // guaranteed directions apply to all of its items.
            allItemsState = getCollapsedThreadItemState(undefined, sureUnseen, sureFlagged);
          } else {
            for (const threadId of uniqueThreadIds) {
              const threadData = queryClient.getQueryData<IListMessagesResponse>([
                'authenticatedQuery',
                currentUserIndex,
                'thread',
                threadId
              ]);
              // The aggregate is only trustworthy when the cached thread contains all of the
              // acted-on messages of the thread.
              const affectedFullyCached =
                !!threadData &&
                specificMessages
                  .filter((message) => message.threadId === threadId)
                  .every((message) =>
                    threadData.results.some(
                      (result) =>
                        result.id === message.messageId && result.mailbox === message.mailboxId
                    )
                  );
              threadItemStates.set(
                threadId,
                getCollapsedThreadItemState(
                  affectedFullyCached ? threadData.results : undefined,
                  sureUnseen,
                  sureFlagged
                )
              );
            }
          }
        }
      }

      // Update list/search caches
      const listQueryKeys: QueryKey[] = dedupeQueryKeys(
        uniqueMailboxIds.flatMap((mailboxId) =>
          getMailboxAndSearchListQueriesKeys(queryClient, currentUserIndex, mailboxId)
        )
      );

      for (const queryKey of listQueryKeys) {
        const isMailboxListKey = queryKey[2] === 'mailbox';
        const oldData = queryClient.getQueryData<IListMessagesResponse>(queryKey);
        if (oldData) {
          snapshots.push({ key: queryKey, data: oldData });
          queryClient.setQueryData<IListMessagesResponse>(queryKey, {
            ...oldData,
            results: oldData.results.map((message) => {
              // Mailbox listings display collapsed threads, so the thread's aggregate state
              // applies; search results are per-message and are updated individually below.
              if (isMailboxListKey && userAttributes.value?.threadingShownState) {
                const state = threadItemStates.get(message.thread) ?? allItemsState;
                if (!state) {
                  return message;
                }
                const updated: IListMessageResponse = { ...message };
                if (state.seen !== null) {
                  updated.seen = state.seen;
                }
                if (state.flagged !== null) {
                  updated.flagged = state.flagged;
                }
                return updated;
              }
              // A thread-scoped read also covers the thread's other messages, which appear
              // as separate rows in search results and non-threaded mailbox listings.
              const isThreadRead = isWholeThreadSeen && affectedThreadIds.has(message.thread);
              if (isMessageAffected(message.id, message.mailbox) || isThreadRead) {
                return { ...message, ...flagUpdates };
              }
              return message;
            })
          });
        }
      }

      // Update singular message caches
      for (const msg of specificMessages) {
        const msgKey: QueryKey = [
          'authenticatedQuery',
          currentUserIndex,
          'mailbox',
          msg.mailboxId,
          'message',
          msg.messageId
        ];
        const oldMsg = queryClient.getQueryData<IMessageResponse>(msgKey);
        if (oldMsg) {
          snapshots.push({ key: msgKey, data: oldMsg });
          queryClient.setQueryData<IMessageResponse>(msgKey, {
            ...oldMsg,
            ...flagUpdates
          });
        }
      }

      // Update folder unseen counters for seen/unseen actions
      if (action === 'seen' || action === 'unseen') {
        updateFolderCounters(
          currentUserIndex,
          snapshots,
          uniqueMailboxIds,
          specificMessages,
          isAllResultsForMailbox,
          action === 'seen' ? 'decrementUnseen' : 'incrementUnseen'
        );
      }
    }

    // --- MOVING ACTIONS ---
    if (isMovingAction(action)) {
      await cancelPendingQueries(uniqueMailboxIds, queryClient, currentUserIndex, uniqueThreadIds);

      const targetMailboxId =
        action === 'moveTo' && 'moveToTarget' in postData ? postData.moveToTarget : undefined;

      // Remove messages from source mailbox lists
      for (const mailboxId of uniqueMailboxIds) {
        const listQueryKeys = getMailboxAndSearchListQueriesKeys(
          queryClient,
          currentUserIndex,
          mailboxId
        );

        for (const queryKey of listQueryKeys) {
          const oldData = queryClient.getQueryData<IListMessagesResponse>(queryKey);
          if (oldData) {
            snapshots.push({ key: queryKey, data: oldData });

            if (isAllResultsForMailbox(mailboxId)) {
              // All results: clear the list
              queryClient.setQueryData<IListMessagesResponse>(queryKey, {
                ...oldData,
                results: [],
                total: 0
              });
            } else {
              const removedIds = new Set(
                specificMessages.filter((m) => m.mailboxId === mailboxId).map((m) => m.messageId)
              );
              const filteredResults = oldData.results.filter(
                (msg) => !removedIds.has(msg.id) || msg.mailbox !== mailboxId
              );

              // If all messages on the current page would be removed and there's
              // another page to navigate to, skip the removal. The page change
              // handler (postMassActionQueuedBus) will navigate to the prev/next
              // page which will fetch fresh data, keeping messages visible with a
              // loader during the transition.
              const hasPagination = !!oldData.previousCursor || !!oldData.nextCursor;
              if (filteredResults.length === 0 && hasPagination) {
                continue;
              }

              queryClient.setQueryData<IListMessagesResponse>(queryKey, {
                ...oldData,
                results: filteredResults,
                total: Math.max(
                  0,
                  oldData.total - (oldData.results.length - filteredResults.length)
                )
              });
            }
          }
        }
      }

      // Update folder counters: decrement source, increment target
      updateFolderCountersForMove(
        currentUserIndex,
        snapshots,
        uniqueMailboxIds,
        specificMessages,
        isAllResultsForMailbox,
        targetMailboxId
      );
    }

    // Return rollback function
    return () => {
      for (const { key, data } of snapshots) {
        queryClient.setQueryData(key, data);
      }
    };
  };

  const updateFolderCounters = (
    currentUserIndex: string,
    snapshots: CacheSnapshot,
    uniqueMailboxIds: string[],
    specificMessages: { mailboxId: string; messageId: number; threadId?: string }[],
    isAllResultsForMailbox: (mailboxId: string) => boolean,
    operation: 'decrementUnseen' | 'incrementUnseen'
  ) => {
    // Count unseen changes per mailbox
    const unseenChanges = new Map<string, number>();

    for (const mailboxId of uniqueMailboxIds) {
      if (isAllResultsForMailbox(mailboxId)) {
        // For all-results, we can't know the exact count, skip folder counter update
        // The server COUNTERS event will handle it
        continue;
      }
      const count = specificMessages.filter((m) => m.mailboxId === mailboxId).length;
      unseenChanges.set(mailboxId, count);
    }

    if (unseenChanges.size === 0) return;

    languageOptions.value.forEach(({ value: locale }) => {
      const folderKey: QueryKey = ['authenticatedQuery', currentUserIndex, 'folders', locale];
      const oldFolders = queryClient.getQueryData<IFolderTreeResponse>(folderKey);
      if (!oldFolders) return;

      snapshots.push({ key: folderKey, data: oldFolders });

      queryClient.setQueryData<IFolderTreeResponse>(folderKey, {
        data: recursiveUpdateFolderUnseen(oldFolders.data, unseenChanges, operation),
        hash: oldFolders.hash
      });
    });
  };

  const updateFolderCountersForMove = (
    currentUserIndex: string,
    snapshots: CacheSnapshot,
    uniqueMailboxIds: string[],
    specificMessages: { mailboxId: string; messageId: number; threadId?: string }[],
    isAllResultsForMailbox: (mailboxId: string) => boolean,
    targetMailboxId: string | undefined
  ) => {
    // Count total and unseen changes per source mailbox
    const totalChanges = new Map<string, number>();
    const unseenChanges = new Map<string, number>();

    for (const mailboxId of uniqueMailboxIds) {
      if (isAllResultsForMailbox(mailboxId)) {
        // Can't know exact count for all-results; server handles it
        continue;
      }
      const msgsInMailbox = specificMessages.filter((m) => m.mailboxId === mailboxId);
      totalChanges.set(mailboxId, msgsInMailbox.length);

      // Count unseen messages by checking the cached list data
      let unseenCount = 0;
      const listKeys = getMailboxAndSearchListQueriesKeys(queryClient, currentUserIndex, mailboxId);
      // Check the first available list cache
      for (const listKey of listKeys) {
        // Snapshots already have old data, check it
        const snapshot = snapshots.find((s) => JSON.stringify(s.key) === JSON.stringify(listKey));
        const listData = snapshot?.data as IListMessagesResponse | undefined;
        if (listData) {
          for (const msg of listData.results) {
            if (
              !msg.seen &&
              msgsInMailbox.some((m) => m.messageId === msg.id && m.mailboxId === msg.mailbox)
            ) {
              unseenCount++;
            }
          }
          break;
        }
      }
      unseenChanges.set(mailboxId, unseenCount);
    }

    if (totalChanges.size === 0) return;

    languageOptions.value.forEach(({ value: locale }) => {
      const folderKey: QueryKey = ['authenticatedQuery', currentUserIndex, 'folders', locale];
      const oldFolders = queryClient.getQueryData<IFolderTreeResponse>(folderKey);
      if (!oldFolders) return;

      // Only snapshot if we haven't already (mark-as may have already snapshotted)
      if (!snapshots.some((s) => JSON.stringify(s.key) === JSON.stringify(folderKey))) {
        snapshots.push({ key: folderKey, data: oldFolders });
      }

      queryClient.setQueryData<IFolderTreeResponse>(folderKey, {
        data: recursiveUpdateFolderCountersForMove(
          oldFolders.data,
          totalChanges,
          unseenChanges,
          targetMailboxId
        ),
        hash: oldFolders.hash
      });
    });
  };

  return { applyOptimisticUpdate };
};

// --- Helper functions for recursive folder counter updates ---

const recursiveUpdateFolderUnseen = (
  folders: IHashedFolderItem[],
  unseenChanges: Map<string, number>,
  operation: 'decrementUnseen' | 'incrementUnseen'
): IHashedFolderItem[] => {
  return folders.map((folder) => {
    const change = unseenChanges.get(folder.id);
    const updated = { ...folder };

    if (change !== undefined) {
      if (operation === 'decrementUnseen') {
        updated.unseen = Math.max(0, folder.unseen - change);
      } else {
        updated.unseen = folder.unseen + change;
      }
    }

    if (folder.children.length > 0) {
      updated.children = recursiveUpdateFolderUnseen(folder.children, unseenChanges, operation);
    }

    return updated;
  });
};

const recursiveUpdateFolderCountersForMove = (
  folders: IHashedFolderItem[],
  totalChanges: Map<string, number>,
  unseenChanges: Map<string, number>,
  targetMailboxId: string | undefined
): IHashedFolderItem[] => {
  return folders.map((folder) => {
    const totalDec = totalChanges.get(folder.id);
    const unseenDec = unseenChanges.get(folder.id);
    const updated = { ...folder };

    // Source folder: decrement
    if (totalDec !== undefined) {
      updated.total = Math.max(0, folder.total - totalDec);
    }
    if (unseenDec !== undefined) {
      updated.unseen = Math.max(0, folder.unseen - unseenDec);
    }

    // Target folder: increment
    if (targetMailboxId && folder.id === targetMailboxId) {
      const totalInc = [...totalChanges.values()].reduce((sum, v) => sum + v, 0);
      const unseenInc = [...unseenChanges.values()].reduce((sum, v) => sum + v, 0);
      updated.total = folder.total + totalInc;
      updated.unseen = folder.unseen + unseenInc;
    }

    if (folder.children.length > 0) {
      updated.children = recursiveUpdateFolderCountersForMove(
        folder.children,
        totalChanges,
        unseenChanges,
        targetMailboxId
      );
    }

    return updated;
  });
};

export default useOptimisticMassAction;
